import { Mail, Sparkles, Target, Users, Compass } from "lucide-react";
import { Link } from "react-router-dom";
import StaticPageLayout from "@/components/StaticPageLayout";

function Section({ icon: Icon, heading, children, testId }) {
  return (
    <section className="mt-10" data-testid={testId}>
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-black/40 border border-white/10">
          <Icon className="h-4 w-4 text-[#00E5FF]" />
        </span>
        <h2 className="font-display text-xl md:text-2xl tracking-tight">
          {heading}
        </h2>
      </div>
      <div className="mt-3 text-white/75 leading-relaxed">{children}</div>
    </section>
  );
}

export default function About() {
  return (
    <StaticPageLayout
      eyebrow="About us"
      title="About Giprompthub"
      description="Your ultimate resource for high-quality, copy-and-paste AI image prompts — tested, categorized, and ready to use."
      metaTitle="About — Giprompthub"
      metaDescription="Learn how Giprompthub curates and tests high-quality AI image prompts for Gemini, Midjourney, and DALL-E 3, and why creators trust our library."
    >
      <p className="text-white/75 leading-relaxed" data-testid="about-body">
        Welcome to <strong className="text-white">Giprompthub.com</strong>, your
        ultimate resource for high-quality, copy-and-paste AI image prompts.
        Our platform is designed to help creators, developers, marketers, and
        everyday AI enthusiasts unlock the full creative potential of modern
        generative AI tools without wasting hours on trial-and-error prompt
        writing. What started as a personal collection of prompts that
        consistently produced striking, professional-grade portraits has grown
        into a fast, searchable library trusted by thousands of monthly
        visitors.
      </p>

      <p className="mt-6 text-white/75 leading-relaxed">
        Our mission is simple: make advanced AI image generation accessible to
        everyone, regardless of technical background. Writing an effective
        prompt is its own skill — one that blends photography vocabulary,
        lighting terminology, camera and lens references, and an
        understanding of how each model interprets language. We do that
        heavy lifting for you, so all you have to do is copy, paste, and
        generate.
      </p>

      <Section
        icon={Target}
        heading="Our Process"
        testId="about-process"
      >
        <p>
          Every prompt published on Giprompthub goes through a repeatable
          testing methodology before it earns a place in our library. We
          start by drafting a prompt around a specific visual concept —
          whether that's a cinematic corporate headshot, a cottagecore
          lifestyle portrait, or a cyberpunk character render. That draft is
          then run multiple times across supported engines to confirm it
          produces consistent, high-quality results rather than a lucky
          one-off image.
        </p>
        <p className="mt-3">
          We currently test and optimize prompts for{" "}
          <strong className="text-white">Google Gemini</strong> (our primary
          engine, given its strong photorealism and reference-image support),{" "}
          <strong className="text-white">Midjourney</strong> (for stylized,
          artistic, and painterly output), and{" "}
          <strong className="text-white">DALL-E 3</strong> (for precise,
          instruction-following compositions). Each prompt card includes a
          plain-language explanation of why the wording works, along with a
          "How to Use" walkthrough and the exact parameters we used, so you
          can adapt it to your own reference photos and use case.
        </p>
      </Section>

      <Section icon={Users} heading="Why Choose Us" testId="about-why-choose-us">
        <p>
          There is no shortage of prompt lists online, but most are
          copy-pasted from one blog to the next without ever being verified.
          Giprompthub is different in three concrete ways: every prompt is
          categorized by visual style so you can browse with intent instead
          of scrolling endlessly; every prompt includes context, not just raw
          text, explaining the photographic or artistic reasoning behind the
          wording; and the library is updated continuously as new styles
          trend and as AI engines evolve their capabilities.
        </p>
        <p className="mt-3">
          We also built the entire experience mobile-first and
          distraction-free — no intrusive pop-ups blocking the prompt you
          came for, no paywalls on core content, and a one-tap "Copy Prompt"
          button on every card so you can move straight from inspiration to
          generation.
        </p>
      </Section>

      <Section icon={Compass} heading="Our Vision" testId="about-vision">
        <p>
          Generative AI is moving fast, and the gap between "knowing a tool
          exists" and "knowing how to get great results from it" is where
          most people get stuck. Our vision is to close that gap by becoming
          the most reliable, no-fluff reference for AI image prompting on the
          web — a place creators bookmark and return to every time they need
          a specific look, rather than starting from a blank page. As new
          models and capabilities launch, we intend to keep testing,
          curating, and expanding the library so Giprompthub remains useful
          for years, not just for this generation of AI tools.
        </p>
      </Section>

      <Section icon={Sparkles} heading="The Creator Behind Giprompthub" testId="about-creator">
        <p>
          Giprompthub was founded by an independent AI prompt engineer and
          content creator with a background in digital photography and visual
          design. After spending countless hours experimenting with
          generative AI tools to recreate specific photographic styles, the
          founder recognized that the biggest barrier to great AI-generated
          images wasn't the technology itself, but the lack of well-tested,
          clearly explained prompts. Giprompthub was built to solve exactly
          that problem, and today it continues to be maintained and expanded
          by a small, hands-on team committed to quality over quantity.
        </p>
      </Section>

      <div className="mt-6 text-white/75 leading-relaxed">
        For queries, suggestions, or feedback, feel free to reach out to us at{" "}
        <a
          href="mailto:support@giprompthub.com"
          className="text-[#00E5FF] hover:text-white underline underline-offset-4"
          data-testid="about-email-link"
        >
          support@giprompthub.com
        </a>
        .
      </div>

      <div className="mt-12 grid sm:grid-cols-2 gap-4">
        <Link
          to="/"
          data-testid="about-cta-gallery"
          className="rounded-xl border border-white/10 hover:border-[#E0007A]/40 bg-[#0E0E12] p-5 group transition-colors"
        >
          <Sparkles className="h-4 w-4 text-[#E0007A]" />
          <div className="font-display text-lg mt-3">Browse the gallery</div>
          <p className="text-sm text-white/50 mt-1">
            Pick a portrait style, copy the exact prompt, paste into Gemini.
          </p>
        </Link>
        <Link
          to="/contact"
          data-testid="about-cta-contact"
          className="rounded-xl border border-white/10 hover:border-[#00E5FF]/40 bg-[#0E0E12] p-5 group transition-colors"
        >
          <Mail className="h-4 w-4 text-[#00E5FF]" />
          <div className="font-display text-lg mt-3">Get in touch</div>
          <p className="text-sm text-white/50 mt-1">
            Questions, suggestions, or business inquiries welcome.
          </p>
        </Link>
      </div>
    </StaticPageLayout>
  );
}
